/**
 * Application Providers and Context Managers
 *
 * Centralizes React context providers for the application, including:
 * - RuntimeConfigProvider: Supplies loaded runtime config to all components
 * - Custom hooks for consuming context (useConfig)
 *
 * All providers wrap the main App component to provide top-level state
 * and configuration that flows down to child components.
 *
 * See: docs/v2/runtime-config.md
 */

import React, {
  createContext,
  useContext,
  useRef,
  useState,
  useEffect,
  ReactNode,
} from "react";
import { Session, User } from "@supabase/supabase-js";
import { toast } from "sonner";
import {
  RuntimeConfig,
  loadRuntimeConfig,
  getMinimalFallbackConfig,
} from "@/lib/config/runtimeConfig";
import { onAuthStateChange } from "@/lib/auth/supabaseAuth";

/**
 * Set by any call site performing an intentional, user-initiated sign-out
 * (see `src/pages/Dashboard.tsx`'s `handleLogout`) immediately before
 * calling Supabase's `signOut()`. `AuthProvider` below consumes this flag
 * to distinguish "the user clicked Sign Out" from "Supabase's own
 * `autoRefreshToken` background refresh failed and force-signed the user
 * out" — both fire an identical `SIGNED_OUT` auth-state-change event, and
 * GoTrue itself does not otherwise expose *why* a session ended. Without
 * this flag, a normal logout would incorrectly also show the "session
 * expired" toast below (see GitHub issue #494 — "E2E: Authentication",
 * "Session Management" test-case group, and
 * `e2e/session-management-auth-flow.spec.ts`, which exercises both
 * branches).
 *
 * Module-scope (not React state) deliberately: it needs to be set from
 * plain event handlers outside of `AuthProvider` itself, and only needs
 * to survive from "user clicks Sign Out" to "the next SIGNED_OUT event a
 * few ms later" — no component needs to read/react to it directly.
 */
let intentionalSignOutInFlight = false;

export function markIntentionalSignOut(): void {
  intentionalSignOutInFlight = true;
}

/**
 * Checks whether a Supabase session was persisted in `localStorage`
 * *before* this render's `getCurrentSession()` call — i.e. "was this
 * browser previously logged in, on a previous page load". Used
 * exclusively for the "already-expired-at-page-load" branch of the
 * "session expired" toast below.
 *
 * Why this is needed in addition to the `onAuthStateChange` listener:
 * `@supabase/auth-js`'s `GoTrueClient.initialize()` deliberately QUEUES
 * every `_notifyAllSubscribers` call (including `SIGNED_OUT` from a
 * failed token refresh) that happens during its own async
 * initialization/`_recoverAndRefresh()`, and only flushes that queue to
 * registered listeners once `initializePromise` has resolved (see that
 * library's own comment: without this, a listener callback awaiting
 * `getSession()` inside its own body would deadlock). `getCurrentSession()`
 * below also awaits that same `initializePromise` — so if a persisted
 * session's refresh token is ALREADY fully expired/rejected at the very
 * first page load, GoTrueClient's own `SIGNED_OUT` notification for that
 * gets flushed and fully delivered before this component's
 * `onAuthStateChange(...)` listener (registered further down, after
 * `getCurrentSession()` returns) ever subscribes — so that specific event
 * is silently missed, no matter how the listener callback is written.
 * This synchronous, pre-`getCurrentSession()` localStorage check is the
 * one part of "was there a session that's now gone" this race can't
 * hide from. (Once the app is already loaded and a session expires
 * *during* active use, the listener path in `initializeAuth()` below
 * handles it correctly on its own — there's no equivalent race there,
 * since the listener is already registered before that later event
 * fires.)
 */
function hasPersistedSupabaseSession(supabaseUrl: string): boolean {
  if (typeof window === "undefined") return false;
  try {
    const ref = new URL(supabaseUrl).hostname.split(".")[0];
    return window.localStorage.getItem(`sb-${ref}-auth-token`) !== null;
  } catch {
    return false;
  }
}

/**
 * React context for runtime configuration
 *
 * Provides loaded config to all child components via useConfig hook.
 * Initially null while config is loading.
 */
const ConfigContext = createContext<RuntimeConfig | null>(null);

/**
 * Auth context interface
 *
 * Provides authentication state to all child components
 */
export interface AuthContextValue {
  session: Session | null;
  user: User | null;
  isLoading: boolean;
  isAuthenticated: boolean;
}

/**
 * React context for authentication
 *
 * Provides current session/user state and loading status
 */
const AuthContext = createContext<AuthContextValue | null>(null);

/**
 * Props for ConfigProvider component
 */
export interface ConfigProviderProps {
  children: ReactNode;
  configPath?: string; // Optional override for config file path (testing)
}

/**
 * ConfigProvider: React Context Provider for runtime configuration
 *
 * Loads runtime configuration on mount and supplies it to all child components
 * via React context. Shows a loading state while config is being fetched.
 *
 * If config loading fails, logs error and uses minimal fallback defaults
 * to allow the app to bootstrap with degraded functionality.
 *
 * @component
 * @example
 * <ConfigProvider>
 *   <App />
 * </ConfigProvider>
 */
export const ConfigProvider: React.FC<ConfigProviderProps> = ({
  children,
  configPath,
}) => {
  const [config, setConfig] = useState<RuntimeConfig | null>(null);
  const [error, setError] = useState<Error | null>(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    /**
     * Load configuration asynchronously
     */
    async function initializeConfig() {
      try {
        console.log("[ConfigProvider] Loading runtime configuration...");
        const loadedConfig = await loadRuntimeConfig(configPath);
        setConfig(loadedConfig);
        setError(null);
        console.log("[ConfigProvider] Runtime config loaded successfully");
      } catch (err) {
        const error = err instanceof Error ? err : new Error(String(err));
        console.error(
          "[ConfigProvider] Failed to load runtime config:",
          error.message,
        );
        console.warn(
          "[ConfigProvider] Using minimal fallback config - some features may be degraded",
        );
        setError(error);
        // Use minimal fallback config to allow app to bootstrap
        setConfig(getMinimalFallbackConfig());
      } finally {
        setLoading(false);
      }
    }

    initializeConfig();
  }, [configPath]);

  // Show loading indicator while config is being fetched
  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-screen bg-gray-50">
        <div className="text-center">
          <div className="mb-4 text-lg font-semibold text-gray-900">
            Loading configuration...
          </div>
          <div className="text-sm text-gray-600">
            Please wait while the application initializes.
          </div>
        </div>
      </div>
    );
  }

  // Provide config via context to children
  return (
    <ConfigContext.Provider value={config}>
      {/* Optionally display error banner if config loading failed but fallback is in use */}
      {error && (
        <div className="bg-yellow-50 border-l-4 border-yellow-400 p-4">
          <div className="flex">
            <div className="flex-shrink-0">
              <svg
                className="h-5 w-5 text-yellow-400"
                xmlns="http://www.w3.org/2000/svg"
                viewBox="0 0 20 20"
                fill="currentColor"
              >
                <path
                  fillRule="evenodd"
                  d="M8.257 3.099c.765-1.36 2.722-1.36 3.486 0l5.58 9.92c.75 1.334-.213 2.98-1.742 2.98H4.42c-1.53 0-2.493-1.646-1.743-2.98l5.58-9.92zM11 13a1 1 0 11-2 0 1 1 0 012 0zm-1-8a1 1 0 00-1 1v3a1 1 0 002 0V6a1 1 0 00-1-1z"
                  clipRule="evenodd"
                />
              </svg>
            </div>
            <div className="ml-3">
              <p className="text-sm font-medium text-yellow-800">
                Configuration Warning: Using fallback defaults. Some features
                may not work as expected.
              </p>
              <p className="text-xs text-yellow-700 mt-1">{error.message}</p>
            </div>
          </div>
        </div>
      )}
      {children}
    </ConfigContext.Provider>
  );
};

/**
 * Hook to access runtime configuration in any component
 *
 * Must be called from a component that is a descendant of ConfigProvider.
 * Throws an error if used outside of ConfigProvider.
 *
 * @returns Loaded runtime configuration
 * @throws Error if called outside ConfigProvider
 *
 * @example
 * function MyComponent() {
 *   const config = useConfig();
 *   return <div>API: {config.apiBaseUrl}</div>;
 * }
 */
export function useConfig(): RuntimeConfig {
  const context = useContext(ConfigContext);
  if (!context) {
    throw new Error(
      "useConfig must be used within a ConfigProvider component. " +
        "Wrap your app with <ConfigProvider><App /></ConfigProvider>",
    );
  }
  return context;
}

/**
 * Optional hook to safely access config with null check
 *
 * Useful for components that may be rendered before config is available.
 * Returns null instead of throwing an error.
 *
 * @returns Loaded config or null if not yet available
 *
 * @example
 * function OptionalComponent() {
 *   const config = useConfigOptional();
 *   if (!config) return <div>Config not ready</div>;
 *   return <div>API: {config.apiBaseUrl}</div>;
 * }
 */
export function useConfigOptional(): RuntimeConfig | null {
  return useContext(ConfigContext);
}

/**
 * Props for AuthProvider component
 */
export interface AuthProviderProps {
  children: ReactNode;
}

/**
 * AuthProvider: React Context Provider for authentication state
 *
 * Monitors Supabase Auth state changes and provides current user/session
 * to all child components via React context.
 *
 * Sets up auth state listener on mount and cleans up on unmount.
 * Shows a loading state while initial session is being restored.
 *
 * @component
 * @example
 * <AuthProvider>
 *   <App />
 * </AuthProvider>
 */
export const AuthProvider: React.FC<AuthProviderProps> = ({ children }) => {
  // AuthProvider is always mounted below ConfigProvider (see AppShell.tsx),
  // so runtime config is guaranteed to be available here.
  const config = useConfig();
  const [session, setSession] = useState<Session | null>(null);
  const [user, setUser] = useState<User | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  // Tracks whether the previous auth-state-change callback saw a session,
  // so a transition to `SIGNED_OUT` can be recognized as "the user WAS
  // signed in a moment ago" — see `markIntentionalSignOut()`'s doc-comment
  // above for why that distinction matters.
  const hadSessionRef = useRef(false);

  useEffect(() => {
    /**
     * Initialize auth state and set up listener
     */
    async function initializeAuth() {
      try {
        // Import here to avoid circular dependency
        const { configureSupabaseClient, getCurrentSession, getCurrentUser } =
          await import("@/lib/auth/supabaseAuth");

        // Point the Supabase client at this environment's project using
        // runtime config (replaces build-time VITE_SUPABASE_* variables —
        // see docs/v2/05-runtime-configuration.md).
        configureSupabaseClient({
          supabaseUrl: config.supabaseUrl,
          supabaseAnonKey: config.supabaseAnonKey,
        });

        // Snapshot BEFORE calling getCurrentSession() — see
        // `hasPersistedSupabaseSession()`'s doc-comment for why this must
        // happen first.
        const hadPersistedSessionBeforeInit = hasPersistedSupabaseSession(config.supabaseUrl);

        // Restore session from storage
        const currentSession = await getCurrentSession();
        const currentUser = await getCurrentUser();

        // "Expired refresh token -> redirect to /login with a 'session
        // expired' toast", specifically for the case where the token was
        // already fully expired/rejected on this very page load (before
        // any `onAuthStateChange` listener could possibly have caught the
        // resulting `SIGNED_OUT` — see `hasPersistedSupabaseSession()`'s
        // doc-comment).
        if (!currentSession && hadPersistedSessionBeforeInit) {
          toast.error("Your session has expired. Please sign in again.");
        }

        setSession(currentSession);
        setUser(currentUser);
        hadSessionRef.current = !!currentSession;

        // Set up listener for auth state changes
        const unsubscribe = onAuthStateChange((event, newSession) => {
          // "Expired refresh token -> redirect to /login with a 'session
          // expired' toast" (GitHub issue #494's "Session Management" test
          // case): a `SIGNED_OUT` event following a previously-established
          // session, that this render didn't itself request via
          // `markIntentionalSignOut()`, means Supabase's own
          // `autoRefreshToken` background retry exhausted the refresh
          // token and force-signed the user out — not a manual logout.
          // The actual redirect to `/login` is handled separately, by
          // `ProtectedRoute` reacting to `isAuthenticated` flipping to
          // `false` (below) — this only owns the toast.
          if (event === "SIGNED_OUT" && hadSessionRef.current && !intentionalSignOutInFlight) {
            toast.error("Your session has expired. Please sign in again.");
          }
          intentionalSignOutInFlight = false;
          hadSessionRef.current = !!newSession;

          setSession(newSession);
          setUser(newSession?.user ?? null);
        });

        setIsLoading(false);

        // Return cleanup function
        return unsubscribe;
      } catch (error) {
        console.error("[AuthProvider] Failed to initialize auth:", error);
        setIsLoading(false);
      }
    }

    let unsubscribe: (() => void) | null | undefined;

    initializeAuth().then((unsub) => {
      unsubscribe = unsub;
    });

    return () => {
      if (unsubscribe) {
        unsubscribe();
      }
    };
  }, [config.supabaseUrl, config.supabaseAnonKey]);

  const value: AuthContextValue = {
    session,
    user,
    isLoading,
    isAuthenticated: !!session && !!user,
  };

  return (
    <AuthContext.Provider value={value}>{children}</AuthContext.Provider>
  );
};

/**
 * Hook to access authentication state in any component
 *
 * Must be called from a component that is a descendant of AuthProvider.
 * Throws an error if used outside of AuthProvider.
 *
 * @returns Current authentication state
 * @throws Error if called outside AuthProvider
 *
 * @example
 * function ProtectedComponent() {
 *   const { user, isAuthenticated } = useAuth();
 *   if (!isAuthenticated) return <div>Not logged in</div>;
 *   return <div>Welcome, {user?.email}</div>;
 * }
 */
export function useAuth(): AuthContextValue {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error(
      "useAuth must be used within an AuthProvider component. " +
        "Wrap your app with <AuthProvider><App /></AuthProvider>"
    );
  }
  return context;
}

/**
 * Optional hook to safely access auth state with null check
 *
 * Useful for components that may be rendered before auth is initialized.
 * Returns null instead of throwing an error.
 *
 * @returns Current auth state or null if not yet available
 *
 * @example
 * function OptionalAuthComponent() {
 *   const auth = useAuthOptional();
 *   if (!auth) return <div>Auth not ready</div>;
 *   return <div>User: {auth.user?.email}</div>;
 * }
 */
export function useAuthOptional(): AuthContextValue | null {
  return useContext(AuthContext);
}
